import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

interface SignUpForm {
  service: 'freelancer' | 'referrer' | 'client' | '';
  name: string;
  email: string;
  username: string;
  password: string;
  referrer_id: string;
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignUpForm>({
    service: '',
    name: '',
    email: '',
    username: '',
    password: '',
    referrer_id: ''
  });
  const [generatedId, setGeneratedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const payload: any = {
        ...formData,
        role: formData.service
      };

      // Only include referrer_id if it's provided and service is client
      if (formData.service === 'client' && formData.referrer_id) {
        payload.referrer_id = formData.referrer_id;
      }

      const response = await axios.post('http://localhost:5000/api/auth/signup', payload);

      if (response.data.success) {
        setGeneratedId(response.data.data.generated_id);
      } else {
        setError(response.data.message || 'Sign up failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/signin');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (generatedId) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Navigation */}
        <nav className="navbar">
          <div className="navbar-container">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            <div className="navbar-actions">
              <button
                onClick={() => navigate('/')}
                className="btn btn-ghost"
              >
                Home
              </button>
              <button 
                onClick={() => navigate('/signin')}
                className="btn btn-ghost"
              >
                Sign in
              </button>
            </div>
          </div>
        </nav>

        {/* Success State - Takes full remaining height and centers content */}
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-8 px-4">
          <div className="w-full max-w-md">
            <div className="auth-card">
              <div className="auth-header">
                <div className="auth-logo">NS</div>
                <h1 className="auth-title">Registration Successful!</h1>
                <p className="auth-subtitle">
                  Your NexSync account has been created
                </p>
              </div>

              <div className="success-message p-6 rounded-lg mb-8">
                <div className="flex items-start mb-4">
                  <svg className="w-6 h-6 text-success mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">Your account has been created successfully.</p>
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Your ID:</p>
                      <div className="id-display mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg font-mono text-lg font-bold text-primary-600 dark:text-primary-400">
                        {generatedId}
                      </div>
                    </div>
                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      <strong className="text-warning">Important:</strong> Please save this ID as you will need it to sign in.
                    </p>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleContinue}
                className="btn btn-primary btn-full"
              >
                Continue to Sign In
              </button>

              <div className="auth-footer mt-8">
                <button
                  onClick={() => navigate('/')}
                  className="back-to-home"
                >
                  ← Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
          </div>
        </div>
      </nav>

      {/* Sign Up Form - Takes full remaining height and centers content */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-8 px-4">
        <div className="w-full max-w-md">
          <div className="auth-card">
            <div className="auth-header">
              <div className="auth-logo">NS</div>
              <h1 className="auth-title">Create Your Account</h1>
              <p className="auth-subtitle">
                Or <Link to="/signin" className="text-primary-600 hover:text-primary-700 font-medium">sign in</Link> to your existing account
              </p>
            </div>

            {error && (
              <div className="error-message mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="form-group">
                  <label htmlFor="service" className="form-label">
                    Service Type *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-input form-select"
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select your role</option>
                    <option value="freelancer">Freelancer</option>
                    <option value="referrer">Referrer</option>
                    <option value="client">Client</option>
                  </select>
                  <div className="form-hint">
                    Choose how you want to use NexSync
                  </div>
                </div>

                {/* Referrer ID Field - Only shown for client role */}
                {formData.service === 'client' && (
                  <div className="form-group">
                    <label htmlFor="referrer_id" className="form-label">
                      Referrer ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="referrer_id"
                      name="referrer_id"
                      value={formData.referrer_id}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter referrer ID if you were referred"
                      disabled={isLoading}
                    />
                    <div className="form-hint">
                      If a referrer introduced you to NexSync, enter their Referrer ID here
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Enter your full name"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Enter your email address"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="username" className="form-label">
                    Username *
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Choose a username"
                    required
                    disabled={isLoading}
                  />
                  <div className="form-hint">
                    This will be your public display name
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password" className="form-label">
                    Password *
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Create a password"
                      required
                      minLength={6}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                      disabled={isLoading}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="form-hint">
                    Password must be at least 6 characters long
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-full mt-8"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="loading-spinner small"></div>
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="auth-footer mt-8">
              <button
                onClick={() => navigate('/')}
                className="back-to-home"
                disabled={isLoading}
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;